import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { AlertTriangle as AlertTriangleIcon } from 'react-feather'

import constants from 'constants/index'
import helpers from 'helpers'
import useStyles from './styles'

const ApplicabilityDeclaration = ({ applicability, evidenceMap }) => {
  const classes = useStyles()

  const { APPLICABILITY_STATUS, APPLICABILITY_STATUS_LABEL } =
    constants.dai.disputes

  if (!applicability || isEmpty(applicability.items)) return null

  const {
    coverage_summary: coverageSummary,
    target_alternative: targetAlternative,
    effective_claimed_letter: effectiveClaimedLetter,
    items,
  } = applicability

  const isMitigation =
    !!effectiveClaimedLetter &&
    !!targetAlternative &&
    effectiveClaimedLetter.toUpperCase() !== targetAlternative.toUpperCase()

  const statusClassName = (status) =>
    clsx(classes.statusChip, {
      [classes.statusAttended]: status === APPLICABILITY_STATUS.ATTENDED,
      [classes.statusPartial]:
        status === APPLICABILITY_STATUS.PARTIALLY_ATTENDED,
    })

  return (
    <Paper variant="outlined" className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          Aderência aos requisitos
        </Typography>

        <Box className={classes.headerActions}>
          {isMitigation && (
            <Tooltip
              title={`A autoavaliação original foi ${targetAlternative}, mas a matriz sustenta a alternativa intermediária ${effectiveClaimedLetter} (amenização).`}
            >
              <Chip
                size="small"
                icon={<AlertTriangleIcon size={12} />}
                label={`Amenização: ${targetAlternative} → ${effectiveClaimedLetter}`}
                className={classes.mitigationChip}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {coverageSummary && (
        <Typography className={classes.coverageSummary}>
          {coverageSummary}
        </Typography>
      )}

      <Box className={classes.tableWrapper}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>Req.</TableCell>
              <TableCell className={classes.headCell}>Status</TableCell>
              <TableCell className={classes.headCell}>Evidência</TableCell>
              <TableCell className={classes.headCell}>Como atende</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const references = (item.satisfied_by || [])
                .map((evidence) =>
                  helpers.dai.formatEvidenceReference(evidence, evidenceMap),
                )
                .filter(Boolean)

              return (
                <TableRow key={item.requirement || index}>
                  <TableCell className={classes.bodyCell}>
                    <Typography className={classes.requirementText}>
                      {item.requirement}
                    </Typography>
                  </TableCell>

                  <TableCell className={classes.bodyCell}>
                    <Chip
                      size="small"
                      label={
                        APPLICABILITY_STATUS_LABEL[item.status] || item.status
                      }
                      className={statusClassName(item.status)}
                    />
                  </TableCell>

                  <TableCell className={classes.bodyCell}>
                    {isEmpty(references) ? (
                      <Typography className={classes.emptyText}>—</Typography>
                    ) : (
                      <Box className={classes.evidenceList}>
                        {references.map((reference, referenceIndex) => (
                          <Typography
                            key={referenceIndex}
                            className={classes.evidenceReference}
                          >
                            {reference}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell className={classes.bodyCell}>
                    <Typography className={classes.howSatisfied}>
                      {item.how_satisfied || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  )
}

ApplicabilityDeclaration.propTypes = {
  applicability: PropTypes.object,
  evidenceMap: PropTypes.object,
}

ApplicabilityDeclaration.defaultProps = {
  evidenceMap: {},
}

export default ApplicabilityDeclaration
